import React, { useMemo, useRef, useState } from 'react';
import WalletCard from '../../../../../../components/WalletCard/WalletCard';
import FlexNairaWl from '../../../../../../assets/icons/MoneyNairaFlex.svg';
import updateMemo from '../../../../../../assets/icons/updateMemo.svg';
import NoData from '../../../../../../components/NoData/NoData';
import dayjs from 'dayjs';
import failureIcon from '../../../../../../assets/icons/failureIcon.svg';
import successIcon from '../../../../../../assets/icons/succesIcon.svg';
import TransactionDetailsAlt from '../../../../../../components/NewSideSheet/TransactionDetailsAlt/TransactionDetailsAlt';
import Dropdown from '../../../../../../components/Dropdown/Dropdown';
import { formatBracketText, formatToNaira, useOutsideClick } from '../../../../../../helpers/utils';
import { LuMoreVertical } from 'react-icons/lu';
import ModalDeleteTransaction from '../../../../../../components/Modals/ModalDeleteTransaction';
import ModalUpdateTransactionMemo from '../../../../../../components/Modals/ModalUpdateTransactionMemo';
import ModalExecuteAction from '../../../../../../components/Modals/ModalExecuteAction';
import ModalTransactionSearch from '../../../../../../components/Modals/ModalTransactionSearch';
import InterestBreakdown from '../../../../../../components/InterestBreakdown/InterestBreakdown';
import { Loader } from '@piggytechng/component-library';
import useFlexNaira from '../../../../../../hooks/useFlexNaira';
import { useNavigate, useParams } from 'react-router-dom';
import useTransaction from '../../../../../../hooks/useTransaction';
import { AppliedTransactionSearch } from '../../../../../../types';

const index = () => {
  const [showWalletBalance] = useState(true);
  const navigate = useNavigate();
  const params = useParams();

  const id = params.id as string;

  const [show, setShow] = useState(false);
  const [trDetails, setTrDetails] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTransactionRef, setSelectedTransactionRef] = useState<string>('');
  const [showUpdateMemoModal, setShowUpdateMemoModal] = useState(false);
  const [selectedTransactionForMemo, setSelectedTransactionForMemo] = useState<{ reference: string; memo: string }>({
    reference: '',
    memo: '',
  });
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string>('');

  const {
    flexNairaBalance,
    isLoadingFlexNairaTransactions,
    flexNairaTransactions,
    refetchFlexNairaTransactions,
    flexNairaAccountNumbers,
    deleteFlexNairaAccount,
    isLoading: isLoadingFlexNaira,
    searchFlexNairaTransactions,
    clearFlexNairaSearch,
    searchedTransactions,
    isSearching,
  } = useFlexNaira();

  const { deleteTransaction, updateTransactionMemo, isLoading: isTransactionLoading } = useTransaction();

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [appliedSearch, setAppliedSearch] = useState<AppliedTransactionSearch | null>(null);
  const [activeHistoryTab, setActiveHistoryTab] = useState<'wallet' | 'interest'>('wallet');

  const transactionsToShow = searchedTransactions ?? flexNairaTransactions?.slice(0, 5);

  const handleApplySearch = (filter: string, value: string, display: string) => {
    setAppliedSearch({ filter, query: value, display });
    searchFlexNairaTransactions(value, filter);
    setShowSearchModal(false);
  };

  const handleResetSearch = () => {
    setAppliedSearch(null);
    clearFlexNairaSearch();
  };

  const [showDropdown, setShowDropdown] = useState({
    index: -1,
    show: false,
  });

  const dropdownRef = useRef(null);
  const actionRef = useRef(null);
  useOutsideClick(dropdownRef, setShowDropdown, actionRef);

  const walletKey = 'flexnaira';

  const flexNairaNubans = useMemo(
    () =>
      Array.isArray(flexNairaAccountNumbers)
        ? flexNairaAccountNumbers.map((account: any, index: number) => ({
            bankName: account.bankName,
            accountNumber: String(account.accountNumber),
            primaryNuban: index === 0,
          }))
        : [],
    [flexNairaAccountNumbers],
  );

  const walletCardsData = useMemo(() => {
    const baseCards = [
      {
        title: 'Flex Naira Balance:',
        balanceKey: flexNairaBalance ?? 0,
        cardId: 'hm-card',
        image: FlexNairaWl,
        isActive: true,
        isOpenSideSheet: false,
      },
    ];

    const primary = flexNairaNubans.find((acc) => acc.primaryNuban) ?? flexNairaNubans[0];
    const nubanCards = primary
      ? [
          {
            title: `Account Number - ${primary.bankName}`,
            cardId: 'fn-acc-card',
            isActive: true,
            isBalance: false,
            isText: true,
            text: primary.accountNumber,
            isAccCard: true,
          },
        ]
      : [];

    return [...baseCards, ...nubanCards];
  }, [flexNairaBalance, flexNairaNubans]);

  const handleDeleteTransaction = (reference: string) => {
    setSelectedTransactionRef(reference);
    setShowDeleteModal(true);
  };

  const handleDeleteAccount = (accountNumber: string) => {
    setSelectedAccountNumber(accountNumber);
    setShowDeleteAccountModal(true);
  };

  const handleDeleteSuccess = () => {
    refetchFlexNairaTransactions();
  };

  const handleUpdateMemo = (reference: string, currentMemo: string) => {
    setSelectedTransactionForMemo({ reference, memo: currentMemo });
    setShowUpdateMemoModal(true);
  };

  const handleUpdateMemoSuccess = () => {
    refetchFlexNairaTransactions();
  };

  const handleShowTransactionsDetails = (data: any) => {
    setShow(true);
    setTrDetails(data);
  };

  const getDropdownArray = (reference: string, currentMemo: string) => [
    {
      icon: updateMemo,
      text: 'Update memo',
      fn: () => handleUpdateMemo(reference, currentMemo),
    },
    {
      icon: updateMemo,
      text: 'Fix transaction',
      fn: () => {},
    },
    {
      icon: updateMemo,
      text: 'Delete transaction',
      fn: () => handleDeleteTransaction(reference),
    },
  ];

  const handleShowDropdown = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, i: number) => {
    e.stopPropagation();
    if (i === showDropdown.index) {
      setShowDropdown({ ...showDropdown, show: false, index: -1 });
    } else {
      setShowDropdown({ ...showDropdown, show: true, index: i });
    }
  };

  return (
    <>
      <div>
        <div className="cards-container flex--2">
          {walletCardsData.map((item: any, i: any) => (
            <WalletCard
              key={i}
              {...item}
              showBalance={showWalletBalance}
              balance={item.balanceKey}
              HMAccounts={flexNairaNubans}
              isAccCardAction={item.isAccCardAction}
              walletKey={walletKey}
              handleDeleteClick={handleDeleteAccount}
            />
          ))}
        </div>
        <div className="wallets-details">
          <div className="history-tab-switch flexnaira">
            <span
              className={`history-pill${activeHistoryTab === 'wallet' ? ' active' : ''}`}
              onClick={() => setActiveHistoryTab('wallet')}
            >
              Wallet History
            </span>
            <span
              className={`history-pill${activeHistoryTab === 'interest' ? ' active' : ''}`}
              onClick={() => setActiveHistoryTab('interest')}
            >
              Interest Breakdown
            </span>
            {activeHistoryTab === 'wallet' && (
              <p className="view-all-g flex-naira" onClick={() => navigate(`/flexnaira-transactions/${id}`)}>
                View all
              </p>
            )}
          </div>
          {activeHistoryTab === 'wallet' && (
            <div className="customers-filter">
              <div className="input-field">
                <input
                  type="text"
                  readOnly
                  placeholder="Search transactions"
                  value={
                    appliedSearch
                      ? `${appliedSearch.filter.charAt(0).toUpperCase() + appliedSearch.filter.slice(1)}: ${
                          appliedSearch.display
                        }`
                      : ''
                  }
                  onClick={() => setShowSearchModal(true)}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <div className="query-filter" onClick={() => setShowSearchModal(true)}>
                <div>
                  <svg width="10" height="10" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M0.75 0.75H17.25M6.75 13.25H11.25M3.75 7H14.25"
                      stroke="#1E293B"
                      strokeWidth="1.5"
                      strokeLinecap="square"
                    />
                  </svg>
                </div>
                <div>Search</div>
              </div>
              <div className="query-filter" onClick={handleResetSearch}>
                <div></div>
                <div>Reset</div>
              </div>
            </div>
          )}
        </div>
        {activeHistoryTab === 'interest' ? (
          <InterestBreakdown walletName="flexnaira" />
        ) : isLoadingFlexNairaTransactions || isSearching ? (
          <div className="page-loader">
            <Loader color="#0F60D6" />
          </div>
        ) : (
          <>
            {transactionsToShow?.length ? (
              <div className="table-container flexnaira">
                <table className="table">
                  <thead className="table_head">
                    <tr>
                      <th className="icon">{''}</th>
                      <th className="title">Title</th>
                      <th className="date">Date</th>
                      <th className="amount">{`Amount (₦)`}</th>
                      <th className="balance">{`Balance (₦)`}</th>
                      <th className="memo">Memo</th>
                      <th className="reference">Reference</th>
                    </tr>
                  </thead>

                  <tbody className="table-body">
                    {transactionsToShow?.map((txn: any, i: any) => (
                      <tr key={txn.reference} className="table-row" onClick={() => handleShowTransactionsDetails(txn)}>
                        <td className="icon">
                          <img
                            src={Number(txn.value) >= 0 ? successIcon : failureIcon}
                            alt={Number(txn.value) >= 0 ? 'credit' : 'debit'}
                          />
                        </td>
                        <td className="title">{formatBracketText(txn.title)}</td>
                        <td className="date">{dayjs(txn.date * 1000).format('D MMM YYYY | h:mm A')}</td>
                        <td className="amount">{formatToNaira(txn.value)}</td>
                        <td className="balance">{formatToNaira(txn.balance)}</td>
                        <td className="memo">{txn.memo}</td>
                        <td className="reference">{txn.reference ?? '-----'}</td>
                        <div className="dropdown-container" ref={actionRef}>
                          <button onClick={(e) => handleShowDropdown(e, i)} className="pointer">
                            <LuMoreVertical color="#CBD5E1" size={25} />
                          </button>

                          {showDropdown.index === i && showDropdown.show && (
                            <div className="table-dropdown" ref={dropdownRef}>
                              <Dropdown data={getDropdownArray(txn.reference, txn.memo || '')} index={i} />
                            </div>
                          )}
                        </div>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <NoData message={'No transaction at the moment.'} />
            )}
          </>
        )}
      </div>

      <ModalTransactionSearch
        modalIsOpen={showSearchModal}
        setIsOpen={setShowSearchModal}
        onApply={handleApplySearch}
        onClear={handleResetSearch}
        isLoading={isSearching}
        initialFilter={appliedSearch?.filter}
        initialQuery={appliedSearch?.query}
      />

      <TransactionDetailsAlt
        show={show}
        which="flexnaira"
        id={''}
        data={trDetails}
        onClose={() => {
          setShow(false);
        }}
      />
      {selectedTransactionRef && (
        <ModalDeleteTransaction
          modalIsOpen={showDeleteModal}
          setIsOpen={setShowDeleteModal}
          reference={selectedTransactionRef}
          wallet="flexnaira"
          onDelete={deleteTransaction}
          onSuccess={handleDeleteSuccess}
          isLoading={isTransactionLoading}
        />
      )}
      {selectedTransactionForMemo.reference && (
        <ModalUpdateTransactionMemo
          modalIsOpen={showUpdateMemoModal}
          setIsOpen={setShowUpdateMemoModal}
          reference={selectedTransactionForMemo.reference}
          wallet="flexnaira"
          currentMemo={selectedTransactionForMemo.memo}
          onUpdate={updateTransactionMemo}
          onSuccess={handleUpdateMemoSuccess}
          isLoading={isTransactionLoading}
        />
      )}
      <ModalExecuteAction
        modalIsOpen={showDeleteAccountModal}
        setIsOpen={setShowDeleteAccountModal}
        text="delete this account number?"
        actionColor="#FF4D4F"
        isLoading={isLoadingFlexNaira}
        action={() => deleteFlexNairaAccount(selectedAccountNumber, () => setShowDeleteAccountModal(false))}
      />
    </>
  );
};

export default index;
